import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";

import { AuthService } from "./auth.service";

/**
 * Fotos de empleado (EntraID / Microsoft Graph) como data-URL, cacheadas por email
 * y compartidas por toda la app (visor de lectura, buscador de personas, avatares).
 * `null` = sin foto (también se cachea para no repetir la llamada).
 */
@Injectable({ providedIn: "root" })
export class EmpleadoFotoService {
	private readonly cache = new Map<string, Observable<string | null>>();

	constructor(private readonly authService: AuthService) {}

	getFoto(email: string | null | undefined): Observable<string | null> {
		const key = (email ?? "").trim().toLowerCase();
		if (!key) {
			return of(null);
		}
		let foto$ = this.cache.get(key);
		if (!foto$) {
			foto$ = this.authService.getUserPhoto(key).pipe(
				switchMap((blob) => this.blobToDataUrl(blob)),
				catchError(() => of(null)),
				shareReplay(1),
			);
			this.cache.set(key, foto$);
		}
		return foto$;
	}

	private blobToDataUrl(blob: Blob | null): Observable<string | null> {
		if (!blob || blob.size === 0) {
			return of(null);
		}
		return new Observable<string | ArrayBuffer | null>((observer) => {
			const reader = new FileReader();
			reader.onload = () => {
				observer.next(reader.result);
				observer.complete();
			};
			reader.onerror = () => {
				observer.next(null);
				observer.complete();
			};
			reader.readAsDataURL(blob);
		}).pipe(map((result) => (typeof result === "string" ? result : null)));
	}
}
